import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    IconButton,
    CircularProgress,
    Breadcrumbs,
    Typography,
    Divider,
    LinearProgress,
} from '@mui/material';
import * as msal from '@azure/msal-browser';
import { Client } from '@microsoft/microsoft-graph-client';
import { BASE_URL } from '../constants';
import {
    Folder,
    File as FileIcon,
    FileText,
    FileImage,
    FileSpreadsheet,
    ChevronLeft,
    X,
    CloudUpload,
    Home,
} from 'lucide-react';


const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (file) => {
    if (!file.file) {
        return <Folder className="text-yellow-500" size={20} />;
    }
    const extension = file.name.split('.').pop().toLowerCase();
    switch (extension) {
        case 'xlsx': case 'xls': case 'csv':
            return <FileSpreadsheet className="text-green-600" size={20} />;
        case 'docx': case 'doc': case 'rtf': case 'txt':
            return <FileText className="text-blue-600" size={20} />;
        case 'pptx': case 'ppt':
            return <FileText className="text-orange-600" size={20} />;
        case 'pdf':
            return <FileText className="text-red-600" size={20} />;
        case 'jpg': case 'jpeg': case 'png': case 'gif': case 'bmp': case 'svg': case 'webp':
            return <FileImage className="text-purple-600" size={20} />;
        default:
            return <FileIcon className="text-gray-500" size={20} />;
    }
};

const msalConfig = {
    auth: {
        clientId: 'cbf73dd3-926d-4069-ac65-d4301dbd9b2b',
        authority: 'https://login.microsoftonline.com/zionclouds.com',
        redirectUri: window.location.origin,
    },
    cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: true,
    },
};

const scopes = [
    'Files.Read',
    'Files.Read.All',
    'Sites.Read.All',
    'User.Read',
];

// Singleton MSAL instance shared across renders
let _msalInstance = null;
let _msalInitialized = false;
let _msalInitPromise = null;

const getMsalInstance = () => {
    if (!_msalInitPromise) {
        _msalInitPromise = (async () => {
            _msalInstance = new msal.PublicClientApplication(msalConfig);
            await _msalInstance.initialize();
            _msalInitialized = true;
            return _msalInstance;
        })();
    }
    return _msalInitPromise;
};

/**
 * MSAccess — Microsoft OneDrive / SharePoint file picker modal.
 *
 * Props:
 *   open        {boolean}   Whether the dialog is open
 *   uploadType  {string}    'onedrive' | 'sharepoint'
 *   userInfo    {object}    { email, name }
 *   showSnackbar {function} Snackbar callback
 *   onClose     {function}  Called when the dialog should close
 */
export const MSAccess = ({ open, uploadType, userInfo, showSnackbar, onClose }) => {
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [msalReady, setMsalReady] = useState(false);
    const [graphClient, setGraphClient] = useState(null);
    const [files, setFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [sharepointSites, setSharepointSites] = useState([]);
    const [selectedSite, setSelectedSite] = useState(null);
    const [libraries, setLibraries] = useState([]);
    const [selectedLibrary, setSelectedLibrary] = useState(null);
    const [uploadStatus, setUploadStatus] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [folderStack, setFolderStack] = useState([]);
    const [currentPath, setCurrentPath] = useState('/');
    const [authError, setAuthError] = useState(null);

    // Initialize MSAL once
    useEffect(() => {
        getMsalInstance().then(() => {
            setMsalReady(true);
            // Check for existing session
            const accounts = _msalInstance.getAllAccounts();
            if (accounts.length > 0) {
                handleAuthResponse(accounts[0]);
            }
        }).catch(err => {
            console.error('MSAL init error:', err);
        });
    }, []);

    // When dialog opens, reset file state
    useEffect(() => {
        if (open) {
            setFiles([]);
            setSelectedFile(null);
            setFolderStack([]);
            setCurrentPath('/');
            setUploadStatus(null);
            setAuthError(null);

            if (uploadType === 'sharepoint') {
                setSelectedSite(null);
                setSelectedLibrary(null);
            }

            // If already authenticated, load files
            if (isAuthenticated && graphClient) {
                if (uploadType === 'onedrive') {
                    loadFiles('onedrive', null, graphClient);
                }
            }
        }
    }, [open, uploadType]);

    const handleLogin = async () => {
        if (!msalReady) return;
        setAuthError(null);
        try {
            const loginResponse = await _msalInstance.loginPopup({
                scopes,
                prompt: 'select_account',
            });
            await handleAuthResponse(loginResponse.account);
        } catch (error) {
            console.error('Login error:', error);
            if (error.errorCode !== 'user_cancelled') {
                setAuthError('Sign-in failed. Please try again.');
            }
        }
    };

    const handleAuthResponse = async (account) => {
        try {
            const tokenResponse = await _msalInstance.acquireTokenSilent({ scopes, account });
            const client = Client.init({
                authProvider: (cb) => cb(null, tokenResponse.accessToken),
            });
            setGraphClient(client);
            setIsAuthenticated(true);

            loadSharePointSites(client);
            if (uploadType === 'onedrive') {
                loadFiles('onedrive', null, client);
            }
        } catch (error) {
            if (error instanceof msal.InteractionRequiredAuthError) {
                try {
                    const tokenResponse = await _msalInstance.acquireTokenPopup({ scopes, account });
                    const client = Client.init({
                        authProvider: (cb) => cb(null, tokenResponse.accessToken),
                    });
                    setGraphClient(client);
                    setIsAuthenticated(true);
                    loadSharePointSites(client);
                    if (uploadType === 'onedrive') {
                        loadFiles('onedrive', null, client);
                    }
                } catch (e) {
                    console.error('Interactive token error:', e);
                    setAuthError('Could not acquire token. Please sign in again.');
                }
            } else {
                console.error('Token error:', error);
            }
        }
    };

    const loadSharePointSites = async (client) => {
        try {
            // First try: sites the user explicitly follows (most secure — user-scoped only)
            let sites = [];
            try {
                const followedRes = await client.api('/me/followedSites').get();
                sites = followedRes.value || [];
            } catch {
                // /me/followedSites may not be available on all tenants
            }

            // Second try: sites the user has recently accessed
            if (sites.length === 0) {
                try {
                    const recentRes = await client.api('/me/insights/used?$filter=ResourceVisualization/Type eq \'spsite\'').get();
                    const siteIds = (recentRes.value || [])
                        .map(i => i.resourceReference?.id)
                        .filter(Boolean);
                    // Deduplicate and fetch site details
                    const unique = [...new Set(siteIds)];
                    const fetched = await Promise.allSettled(
                        unique.map(id => client.api(`/sites/${id}`).get())
                    );
                    sites = fetched
                        .filter(r => r.status === 'fulfilled')
                        .map(r => r.value);
                } catch {
                    // insights API may not be enabled
                }
            }

            // Last resort: search only within the user's own MySite host
            // (still scoped — avoids returning all tenant sites)
            if (sites.length === 0) {
                try {
                    const meRes = await client.api('/me').get();
                    const mySiteUrl = meRes.mySite; // e.g. https://tenant-my.sharepoint.com/personal/user
                    if (mySiteUrl) {
                        const hostname = new URL(mySiteUrl).hostname;
                        const res = await client.api(`/sites/${hostname}:/sites`).get();
                        sites = res.value || [];
                    }
                } catch {
                    // fallback failed
                }
            }

            setSharepointSites(sites);
        } catch (error) {
            console.error('Error loading SharePoint sites:', error);
        }
    };

    const loadLibraries = async (siteId, client) => {
        const c = client || graphClient;
        if (!c) return;
        try {
            setIsLoading(true);
            const response = await c.api(`/sites/${siteId}/drives`).get();
            setLibraries(response.value || []);
        } catch (error) {
            console.error('Error loading libraries:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadFiles = async (source, libraryId = null, client = null) => {
        const c = client || graphClient;
        if (!c) return;
        try {
            setIsLoading(true);
            setFiles([]);
            setCurrentPath('/');
            setFolderStack([]);
            let response;
            if (source === 'onedrive') {
                response = await c.api('/me/drive/root/children').get();
            } else if (source === 'sharepoint' && libraryId) {
                response = await c.api(`/drives/${libraryId}/root/children`).get();
            } else {
                return;
            }
            setFiles(response?.value || []);
        } catch (error) {
            console.error(`Error loading files from ${source}:`, error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSiteChange = (event) => {
        const site = sharepointSites.find(s => s.id === event.target.value);
        setSelectedSite(site);
        setSelectedLibrary(null);
        setFiles([]);
        if (site) loadLibraries(site.id);
    };

    const handleLibraryChange = (event) => {
        const library = libraries.find(l => l.id === event.target.value);
        setSelectedLibrary(library);
        if (library) loadFiles('sharepoint', library.id);
    };

    const handleFolderClick = async (folder) => {
        if (!folder.folder) return;
        try {
            setIsLoading(true);
            setFolderStack(prev => [...prev, { id: folder.id, name: folder.name, path: currentPath }]);
            setCurrentPath(`${currentPath}${folder.name}/`);
            let response;
            if (uploadType === 'onedrive') {
                response = await graphClient.api(`/me/drive/items/${folder.id}/children`).get();
            } else if (selectedLibrary) {
                response = await graphClient.api(`/drives/${selectedLibrary.id}/items/${folder.id}/children`).get();
            }
            setFiles(response?.value || []);
        } catch (error) {
            console.error('Error loading folder:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const navigateBack = async () => {
        if (folderStack.length === 0) return;
        try {
            setIsLoading(true);
            const prevFolder = folderStack[folderStack.length - 1];
            const newStack = folderStack.slice(0, -1);
            setFolderStack(newStack);
            setCurrentPath(prevFolder.path);
            let response;
            if (newStack.length === 0) {
                if (uploadType === 'onedrive') {
                    response = await graphClient.api('/me/drive/root/children').get();
                } else if (selectedLibrary) {
                    response = await graphClient.api(`/drives/${selectedLibrary.id}/root/children`).get();
                }
            } else {
                const parentFolder = newStack[newStack.length - 1];
                if (uploadType === 'onedrive') {
                    response = await graphClient.api(`/me/drive/items/${parentFolder.id}/children`).get();
                } else if (selectedLibrary) {
                    response = await graphClient.api(`/drives/${selectedLibrary.id}/items/${parentFolder.id}/children`).get();
                }
            }
            setFiles(response?.value || []);
        } catch (error) {
            console.error('Error navigating back:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile || !graphClient) return;
        try {
            setIsUploading(true);
            setUploadStatus('Downloading from Microsoft...');

            const fileInfoEndpoint = uploadType === 'onedrive'
                ? `/me/drive/items/${selectedFile.id}`
                : `/drives/${selectedLibrary.id}/items/${selectedFile.id}`;

            const fileInfo = await graphClient.api(fileInfoEndpoint).get();

            if (!fileInfo['@microsoft.graph.downloadUrl']) {
                throw new Error('Download URL not available');
            }

            const downloadResponse = await fetch(fileInfo['@microsoft.graph.downloadUrl']);
            const fileBlob = await downloadResponse.blob();
            const file = new File(
                [fileBlob],
                selectedFile.name,
                { type: selectedFile.file?.mimeType || 'application/octet-stream' }
            );

            setUploadStatus('Uploading & Processing...');

            const formData = new FormData();
            formData.append('file', file);
            const uploadResponse = await fetch(
                `${BASE_URL}/api/documents/upload?user_id=${encodeURIComponent(userInfo?.email)}`,
                { method: 'POST', body: formData }
            );

            if (!uploadResponse.ok) throw new Error('Upload failed');

            setUploadStatus('Upload complete!');
            showSnackbar({
                message: 'File uploaded successfully! Processing will begin shortly.',
                severity: 'success',
            });

            setTimeout(() => {
                onClose?.();
                navigate('/files');
            }, 800);
        } catch (error) {
            console.error('Upload error:', error);
            setUploadStatus(`Error: ${error.message}`);
            setTimeout(() => setUploadStatus(null), 5000);
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        if (!isUploading) onClose?.();
    };

    const serviceLabel = uploadType === 'onedrive' ? 'OneDrive' : 'SharePoint';
    const serviceColor = uploadType === 'onedrive' ? '#0078d4' : '#038387';

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    minHeight: '560px',
                    display: 'flex',
                    flexDirection: 'column',
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    pb: 2,
                    background: `linear-gradient(135deg, ${serviceColor}10, transparent)`,
                }}
            >
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        background: serviceColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CloudUpload size={20} color="white" />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        Browse {serviceLabel}
                    </Typography>
                    {isAuthenticated && (
                        <Typography variant="caption" color="text.secondary">
                            Select a file to upload
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={handleClose} disabled={isUploading} size="small">
                    <X size={18} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
                {!isAuthenticated ? (
                    /* ── Sign-in screen ── */
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 3,
                            p: 4,
                        }}
                    >
                        <Box
                            sx={{
                                width: 72,
                                height: 72,
                                borderRadius: '16px',
                                background: `${serviceColor}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `2px solid ${serviceColor}30`,
                            }}
                        >
                            <CloudUpload size={36} color={serviceColor} />
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                Connect to {serviceLabel}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Sign in with your Microsoft account to browse and select files from {serviceLabel}.
                            </Typography>
                        </Box>
                        {authError && (
                            <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
                                {authError}
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleLogin}
                            disabled={!msalReady}
                            sx={{
                                background: serviceColor,
                                borderRadius: '10px',
                                px: 4,
                                py: 1.5,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                '&:hover': { background: serviceColor, filter: 'brightness(0.9)' },
                            }}
                            startIcon={
                                !msalReady
                                    ? <CircularProgress size={16} color="inherit" />
                                    : null
                            }
                        >
                            {msalReady ? `Sign in with Microsoft` : 'Initializing...'}
                        </Button>
                    </Box>
                ) : (
                    /* ── File browser ── */
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        {/* SharePoint site/library selectors */}
                        {uploadType === 'sharepoint' && (
                            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', gap: 2 }}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>SharePoint Site</InputLabel>
                                    <Select
                                        value={selectedSite?.id || ''}
                                        onChange={handleSiteChange}
                                        label="SharePoint Site"
                                    >
                                        <MenuItem value=""><em>Select a site</em></MenuItem>
                                        {sharepointSites.map(site => (
                                            <MenuItem key={site.id} value={site.id}>
                                                {site.displayName || site.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {selectedSite && (
                                    <FormControl size="small" fullWidth>
                                        <InputLabel>Document Library</InputLabel>
                                        <Select
                                            value={selectedLibrary?.id || ''}
                                            onChange={handleLibraryChange}
                                            label="Document Library"
                                        >
                                            <MenuItem value=""><em>Select a library</em></MenuItem>
                                            {libraries.map(lib => (
                                                <MenuItem key={lib.id} value={lib.id}>
                                                    {lib.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Box>
                        )}

                        {/* Breadcrumb / navigation bar */}
                        <Box
                            sx={{
                                px: 2,
                                py: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                bgcolor: '#f8fafc',
                                minHeight: 44,
                            }}
                        >
                            {folderStack.length > 0 && (
                                <IconButton size="small" onClick={navigateBack} sx={{ mr: 0.5 }}>
                                    <ChevronLeft size={16} />
                                </IconButton>
                            )}
                            <Home size={14} className="text-gray-400" />
                            <Breadcrumbs separator="›" sx={{ fontSize: '0.8rem' }}>
                                <Typography
                                    variant="caption"
                                    sx={{ color: folderStack.length === 0 ? serviceColor : 'text.secondary', cursor: 'pointer', fontWeight: 600 }}
                                    onClick={() => {
                                        if (folderStack.length > 0) {
                                            setFolderStack([]);
                                            setCurrentPath('/');
                                            loadFiles(uploadType, selectedLibrary?.id);
                                        }
                                    }}
                                >
                                    {serviceLabel}
                                </Typography>
                                {folderStack.map((f, i) => (
                                    <Typography
                                        key={f.id}
                                        variant="caption"
                                        sx={{
                                            color: i === folderStack.length - 1 ? serviceColor : 'text.secondary',
                                            fontWeight: i === folderStack.length - 1 ? 700 : 400,
                                        }}
                                    >
                                        {f.name}
                                    </Typography>
                                ))}
                            </Breadcrumbs>
                        </Box>

                        {/* Loading bar */}
                        {isLoading && <LinearProgress sx={{ height: 2 }} />}

                        {/* File list */}
                        <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                            {!isLoading && files.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                                    <Folder size={40} className="mx-auto mb-2 opacity-30" />
                                    <Typography variant="body2">
                                        {uploadType === 'sharepoint' && !selectedSite
                                            ? 'Select a SharePoint site to browse files.'
                                            : uploadType === 'sharepoint' && !selectedLibrary
                                                ? 'Select a document library to browse files.'
                                                : 'This folder is empty.'}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                                        gap: 1,
                                        p: 1,
                                    }}
                                >
                                    {files.map(file => {
                                        const isFolder = !!file.folder;
                                        const isSelected = selectedFile?.id === file.id;
                                        return (
                                            <Box
                                                key={file.id}
                                                onClick={() => isFolder ? handleFolderClick(file) : setSelectedFile(file)}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: 0.75,
                                                    p: 1.5,
                                                    borderRadius: '10px',
                                                    cursor: 'pointer',
                                                    border: '2px solid',
                                                    borderColor: isSelected ? serviceColor : 'transparent',
                                                    bgcolor: isSelected ? `${serviceColor}10` : 'transparent',
                                                    transition: 'all 0.15s ease',
                                                    '&:hover': {
                                                        bgcolor: isSelected ? `${serviceColor}15` : '#f1f5f9',
                                                        borderColor: isSelected ? serviceColor : '#e2e8f0',
                                                    },
                                                }}
                                            >
                                                <Box sx={{ fontSize: 32, lineHeight: 1 }}>
                                                    {getFileIcon(file)}
                                                </Box>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        textAlign: 'center',
                                                        wordBreak: 'break-word',
                                                        lineHeight: 1.3,
                                                        maxWidth: '100%',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        fontWeight: isSelected ? 600 : 400,
                                                    }}
                                                >
                                                    {file.name}
                                                </Typography>
                                                {!isFolder && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                        {formatFileSize(file.size)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Upload progress overlay */}
                {uploadStatus && (
                    <Box
                        sx={{
                            px: 2,
                            py: 1.5,
                            bgcolor: uploadStatus.startsWith('Error') ? '#fef2f2' : '#f0fdf4',
                            borderTop: '1px solid',
                            borderColor: uploadStatus.startsWith('Error') ? '#fecaca' : '#bbf7d0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        {isUploading && <CircularProgress size={14} />}
                        <Typography variant="caption" color={uploadStatus.startsWith('Error') ? 'error' : 'success.main'}>
                            {uploadStatus}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            {/* Footer actions */}
            {isAuthenticated && (
                <>
                    <Divider />
                    <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                        {selectedFile && (
                            <Typography variant="body2" color="text.secondary" sx={{ flex: 1, mr: 1 }}>
                                <strong>Selected:</strong> {selectedFile.name}
                            </Typography>
                        )}
                        <Button
                            onClick={handleClose}
                            disabled={isUploading}
                            variant="outlined"
                            sx={{ borderRadius: '8px', textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUpload}
                            disabled={!selectedFile || isUploading}
                            variant="contained"
                            sx={{
                                background: serviceColor,
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 3,
                                '&:hover': { background: serviceColor, filter: 'brightness(0.9)' },
                                '&.Mui-disabled': { opacity: 0.5 },
                            }}
                            startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUpload size={16} />}
                        >
                            {isUploading ? 'Uploading & Processing...' : 'Upload Selected File'}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};